import { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Loader2, ExternalLink, Send, Clock, AlertTriangle, Trophy } from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Toaster } from "@/components/ui/sonner";
import { ChatPanel } from "@/components/ChatPanel";

export default function RfqPortal() {
  const { token } = useParams();
  const [rfq, setRfq] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const [form, setForm] = useState({
    supplier_name: "",
    supplier_email: "",
    supplier_phone: "",
    contact_person: "",
    lead_time_days: 0,
    validity: "",
    notes: "",
    currency: "",  // set once RFQ loads
    linePrices: {},
  });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setBusy(true);
      setErr(null);
      try {
        const data = await api.getPublicRfq(token);
        if (cancelled) return;
        setRfq(data);
        // Seed line prices for each line
        setForm((f) => ({
          ...f,
          currency: f.currency || data.currency || "USD",
          linePrices: Object.fromEntries(
            (data.lines || []).map((_, i) => [
              i, { unit_price: 0, lead_time_days: 0, notes: "" },
            ]),
          ),
        }));
      } catch (e) {
        if (!cancelled) {
          setErr(e?.response?.data?.detail || "This RFQ link is invalid or has expired.");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    if (token) load();
    return () => { cancelled = true; };
  }, [token]);

  const total = useMemo(() => {
    if (!rfq) return 0;
    return (rfq.lines || []).reduce((sum, ln, i) => {
      const price = Number(form.linePrices[i]?.unit_price || 0);
      const qty = Number(ln.qty || 0);
      return sum + price * qty;
    }, 0);
  }, [rfq, form.linePrices]);

  async function submit() {
    if (!form.supplier_name.trim()) {
      toast.error("Please enter your company name");
      return;
    }
    const lines = Object.entries(form.linePrices)
      .filter(([, v]) => Number(v?.unit_price || 0) > 0)
      .map(([idx, v]) => ({
        line_index: Number(idx),
        unit_price: Number(v.unit_price || 0),
        lead_time_days: Number(v.lead_time_days || 0),
        notes: (v.notes || "").trim(),
      }));
    if (lines.length === 0) {
      toast.error("Add a price to at least one line");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.submitPublicRfqQuote(token, {
        supplier_name: form.supplier_name.trim(),
        supplier_email: form.supplier_email.trim(),
        supplier_phone: form.supplier_phone.trim(),
        contact_person: form.contact_person.trim(),
        currency: form.currency || rfq.currency || "USD",
        lead_time_days: Number(form.lead_time_days || 0),
        validity: form.validity || null,
        notes: form.notes.trim(),
        lines,
      });
      setSubmitted(res);
      const beat = Array.isArray(res?.best_bid_lines) ? res.best_bid_lines.length : 0;
      if (beat > 0) {
        toast.success(
          `Nice work — you beat the previous best on ${beat} item${beat === 1 ? "" : "s"}!`,
          { duration: 6000 },
        );
      } else {
        toast.success("Thank you — your quotation has been submitted.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (busy) {
    return (
      <FullPage>
        <div className="grid place-items-center h-64">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      </FullPage>
    );
  }
  if (err) {
    return (
      <FullPage>
        <div className="max-w-md mx-auto text-center py-20">
          <p className="text-lg font-semibold text-red-700">Unable to load RFQ</p>
          <p className="text-sm text-muted-foreground mt-2">{err}</p>
        </div>
      </FullPage>
    );
  }
  if (submitted) {
    const beat = Array.isArray(submitted.best_bid_lines) ? submitted.best_bid_lines : [];
    const savings = Array.isArray(submitted.best_bid_savings) ? submitted.best_bid_savings : [];
    return (
      <FullPage>
        <div className="max-w-lg mx-auto text-center py-16" data-testid="rfq-portal-submitted">
          <span className="inline-grid place-items-center size-14 bg-emerald-100 rounded-full">
            <CheckCircle2 className="size-7 text-emerald-700" />
          </span>
          <h1 className="mt-4 text-2xl font-bold">Quotation received</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Thanks for quoting <span className="font-mono font-semibold">{submitted.rfq_number}</span>.
            KINOS has recorded a total of{" "}
            <span className="mono font-semibold">
              {Number(submitted.subtotal || 0).toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })} {submitted.currency}
            </span>{" "}
            for your bid and will get back to you soon.
          </p>
          {beat.length > 0 && (
            <div
              data-testid="rfq-portal-best-bid-banner"
              data-beaten-count={beat.length}
              className="mt-6 mx-auto max-w-md rounded-md border border-emerald-300 bg-emerald-50 p-4 text-left"
            >
              <p className="flex items-center gap-2 text-emerald-800 font-semibold text-sm">
                <Trophy className="size-4" />
                You beat the previous best on {beat.length} item{beat.length === 1 ? "" : "s"}
              </p>
              {savings.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-emerald-900/80">
                  {savings.map((s) => (
                    <li key={s.line_index} data-testid={`rfq-portal-beat-line-${s.line_index}`}>
                      • {s.item_name || `Line #${s.line_index + 1}`} — saving{" "}
                      <span className="mono font-semibold">
                        {Number(s.savings || 0).toLocaleString(undefined, {
                          minimumFractionDigits: 2, maximumFractionDigits: 2,
                        })} {submitted.currency}
                      </span>{" "}
                      vs previous best
                    </li>
                  ))}
                </ul>
              )}
              <p className="mt-2 text-[11px] text-emerald-800/70">
                The KINOS procurement team has been notified.
              </p>
            </div>
          )}
        </div>
        <Toaster richColors position="top-right" />
      </FullPage>
    );
  }

  return (
    <FullPage>
      <div className="max-w-4xl mx-auto py-8" data-testid="rfq-portal-page">
        <div className="flex items-center justify-between border-b border-border pb-4 mb-6">
          <div>
            <p className="overline">Request for Quotation</p>
            <h1 className="text-3xl font-bold mt-1 mono text-primary">{rfq.rfq_number}</h1>
            <p className="text-xs text-muted-foreground mt-1">
              {rfq.reference ? `Reference: ${rfq.reference} · ` : ""}
              Issue date: {rfq.issue_date}
              {rfq.valid_until && ` · Valid until: ${rfq.valid_until}`}
            </p>
          </div>
          <a
            href={api.publicRfqPdfUrl(token)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-primary underline"
            data-testid="rfq-portal-open-pdf"
          >
            <ExternalLink className="size-4" /> Open printable PDF
          </a>
        </div>

        {rfq.notes && (
          <div className="rounded-md bg-slate-50 border border-border p-3 mb-6 text-sm whitespace-pre-wrap">
            {rfq.notes}
          </div>
        )}

        <BidCountdownBanner validUntil={rfq.valid_until} />

        <h2 className="text-lg font-semibold mb-3">Your quote</h2>
        <div className="border border-border rounded-md overflow-hidden mb-6">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b border-border">
                <TableHead className="overline">#</TableHead>
                <TableHead className="overline">Item</TableHead>
                <TableHead className="overline text-right">Qty</TableHead>
                <TableHead className="overline">Unit</TableHead>
                <TableHead className="overline text-right">Unit price ({form.currency || rfq.currency})</TableHead>
                <TableHead className="overline text-right">Lead time (days)</TableHead>
                <TableHead className="overline text-right">Line total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(rfq.lines || []).map((ln, i) => {
                const price = Number(form.linePrices[i]?.unit_price || 0);
                const lineTotal = price * Number(ln.qty || 0);
                return (
                  <TableRow key={i} data-testid={`rfq-portal-line-${i}`}>
                    <TableCell className="mono">{i + 1}</TableCell>
                    <TableCell>
                      <div className="font-medium">{ln.name}</div>
                      {ln.specification && (
                        <div className="text-[11px] text-muted-foreground whitespace-pre-wrap mt-0.5">
                          {ln.specification}
                        </div>
                      )}
                      {(ln.brand || ln.model) && (
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {[ln.brand, ln.model].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="text-right mono">{ln.qty}</TableCell>
                    <TableCell className="text-xs">{ln.unit || "unit"}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        data-testid={`rfq-portal-line-${i}-price`}
                        type="number" min="0" step="0.01"
                        value={form.linePrices[i]?.unit_price || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            linePrices: {
                              ...f.linePrices,
                              [i]: {
                                ...(f.linePrices[i] || {}),
                                unit_price: e.target.value,
                              },
                            },
                          }))
                        }
                        className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        data-testid={`rfq-portal-line-${i}-lead`}
                        type="number" min="0" step="1"
                        value={form.linePrices[i]?.lead_time_days || ""}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            linePrices: {
                              ...f.linePrices,
                              [i]: {
                                ...(f.linePrices[i] || {}),
                                lead_time_days: e.target.value,
                              },
                            },
                          }))
                        }
                        className="h-8 text-right"
                      />
                    </TableCell>
                    <TableCell className="text-right mono font-semibold">
                      {lineTotal.toLocaleString(undefined, {
                        minimumFractionDigits: 2, maximumFractionDigits: 2,
                      })} {form.currency || rfq.currency}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <div className="flex justify-end mb-6">
          <div className="text-right">
            <p className="overline text-primary">Total quote</p>
            <p className="mono text-3xl font-bold text-primary mt-1" data-testid="rfq-portal-total">
              {total.toLocaleString(undefined, {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
              })}{" "}
              {form.currency || rfq.currency}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-3 border border-border rounded-md p-4 bg-slate-50">
          <div className="col-span-12 sm:col-span-6">
            <Label className="overline">Your company name *</Label>
            <Input
              data-testid="rfq-portal-supplier-name"
              value={form.supplier_name}
              onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
              placeholder="Acme Supplies Ltd"
            />
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label className="overline">Contact person</Label>
            <Input
              data-testid="rfq-portal-contact"
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
            />
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label className="overline">Email</Label>
            <Input
              data-testid="rfq-portal-email"
              type="email"
              value={form.supplier_email}
              onChange={(e) => setForm({ ...form, supplier_email: e.target.value })}
            />
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label className="overline">Phone</Label>
            <Input
              data-testid="rfq-portal-phone"
              value={form.supplier_phone}
              onChange={(e) => setForm({ ...form, supplier_phone: e.target.value })}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Label className="overline">Overall lead time (days)</Label>
            <Input
              data-testid="rfq-portal-lead"
              type="number" min="0"
              value={form.lead_time_days}
              onChange={(e) => setForm({ ...form, lead_time_days: e.target.value })}
            />
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Label className="overline">Quote currency</Label>
            <Select
              value={form.currency || rfq.currency}
              onValueChange={(v) => setForm({ ...form, currency: v })}
            >
              <SelectTrigger data-testid="rfq-portal-currency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["USD", "EUR", "GBP", "AUD", "SGD", "IDR"].map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.currency && form.currency !== rfq.currency && (
              <p className="text-[10px] text-muted-foreground mt-1">
                KINOS will auto-convert to {rfq.currency} for comparison.
              </p>
            )}
          </div>
          <div className="col-span-6 sm:col-span-3">
            <Label className="overline">Quote valid until</Label>
            <Input
              data-testid="rfq-portal-validity"
              type="date"
              value={form.validity}
              onChange={(e) => setForm({ ...form, validity: e.target.value })}
            />
          </div>
          <div className="col-span-12 sm:col-span-6">
            <Label className="overline">Notes / payment terms / warranty</Label>
            <Textarea
              data-testid="rfq-portal-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="e.g. Net 30, 12-month warranty, prices FOB Dili"
            />
          </div>
        </div>

        <div className="flex justify-end mt-5">
          <Button
            data-testid="rfq-portal-submit"
            disabled={submitting}
            onClick={submit}
            className="bg-primary hover:bg-primary/90"
            size="lg"
          >
            {submitting
              ? <Loader2 className="size-4 mr-1.5 animate-spin" />
              : <Send className="size-4 mr-1.5" />}
            Submit quotation
          </Button>
        </div>

        <div className="mt-8">
          <SupplierChat token={token} defaultSupplierName={form.supplier_name} />
        </div>
      </div>
      <Toaster richColors position="top-right" />
    </FullPage>
  );
}

function FullPage({ children }) {
  return (
    <div className="min-h-screen bg-white text-foreground p-4 sm:p-6">
      <div className="max-w-5xl mx-auto flex items-center gap-3 pb-6 border-b border-border">
        <img src="/kinos-logo.jpg" alt="KINOS" className="h-10 w-auto object-contain" />
        <div>
          <p className="text-[10px] tracking-wider uppercase text-muted-foreground">
            KINOS · Leading Innovation Technology
          </p>
          <p className="text-xs text-muted-foreground">Supplier Portal</p>
        </div>
      </div>
      {children}
    </div>
  );
}


function BidCountdownBanner({ validUntil }) {
  if (!validUntil) return null;
  const today = new Date(new Date().toISOString().slice(0, 10));
  const target = new Date(String(validUntil).slice(0, 10));
  if (Number.isNaN(target.getTime())) return null;
  const diff = Math.round((target - today) / (24 * 3600 * 1000));

  let tone;
  let title;
  let sub;
  let Icon = Clock;
  if (diff < 0) {
    tone = "bg-red-50 border-red-300 text-red-800";
    Icon = AlertTriangle;
    title = "Submission window closed";
    sub = `The deadline was ${validUntil}. You can still submit but KINOS will confirm whether late bids are accepted.`;
  } else if (diff === 0) {
    tone = "bg-red-50 border-red-300 text-red-800";
    Icon = AlertTriangle;
    title = "Closes today";
    sub = `Please submit your quotation before the end of ${validUntil}.`;
  } else if (diff <= 3) {
    tone = "bg-amber-50 border-amber-300 text-amber-900";
    title = `Only ${diff} day${diff === 1 ? "" : "s"} left to submit`;
    sub = `Bids close on ${validUntil}. Get your prices in soon.`;
  } else if (diff <= 7) {
    tone = "bg-sky-50 border-sky-300 text-sky-900";
    title = `${diff} days left to submit`;
    sub = `Bids close on ${validUntil}. Take your time to price this well.`;
  } else {
    tone = "bg-emerald-50 border-emerald-300 text-emerald-900";
    title = `${diff} days left to submit`;
    sub = `Bids close on ${validUntil}.`;
  }
  return (
    <div
      data-testid="rfq-portal-countdown"
      className={`border rounded-md p-4 mb-6 flex items-center gap-3 ${tone}`}
    >
      <Icon className="size-5 shrink-0" />
      <div>
        <p className="font-semibold text-sm">{title}</p>
        <p className="text-xs opacity-80 mt-0.5">{sub}</p>
      </div>
    </div>
  );
}

function SupplierChat({ token, defaultSupplierName }) {
  const [supplierName, setSupplierName] = useState(defaultSupplierName || "");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (defaultSupplierName) setSupplierName(defaultSupplierName);
  }, [defaultSupplierName]);

  const { data, isRefetching, refetch } = useQuery({
    queryKey: ["public-rfq-messages", token],
    queryFn: () => api.getPublicRfqMessages(token),
    enabled: !!token,
    refetchInterval: 15_000,
    refetchIntervalInBackground: false,
  });
  const messages = (data && data.messages) || [];

  return (
    <ChatPanel
      messages={messages}
      viewerRole="supplier"
      isSending={sending}
      isRefetching={isRefetching}
      testIdPrefix="rfq-portal-chat"
      disabled={!supplierName.trim()}
      disabledHint="Enter your company name to send a message."
      supplierNameField={
        <Input
          data-testid="rfq-portal-chat-name"
          value={supplierName}
          onChange={(e) => setSupplierName(e.target.value)}
          placeholder="Your company name"
          className="h-8 text-xs"
        />
      }
      onSend={async (text) => {
        setSending(true);
        try {
          await api.postPublicRfqMessage(token, {
            supplier_name: supplierName.trim(),
            text,
          });
          await refetch();
        } finally {
          setSending(false);
        }
      }}
    />
  );
}
